import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSocietyAdminData,
  getBankAccountSummary,
  BankAccountSummaryEntry,
} from '../services/api';
import {
  HiDocumentText,
  HiRefresh,
  HiExclamation,
  HiChevronRight,
  HiUpload,
  HiX,
  HiCheckCircle,
  HiDotsVertical,
  HiSearch,
  HiEye,
  HiEyeOff,
} from '../components/Icons';
import { 
  unlinkBill, 
  API_BASE_URL, 
  updateTransactionTitle, 
  markStatementAsSettled,
  ignoreTransaction,
  parseStatementPdf,
  saveBankAccountSummary,
  bulkIgnoreTransactions,
} from '../services/api';
import './SocietyAdminLedger.css';

const PAGE_SIZE = 100;

const SocietyAdminLedger: React.FC = () => {
  const navigate = useNavigate();
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;
  const societyName = adminData?.societyName || 'Society';

  // OCR processing
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [processingPdf, setProcessingPdf] = useState(false);
  const [parsedTransactions, setParsedTransactions] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);

  const [entries, setEntries] = useState<BankAccountSummaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);
  const [totalDebits, setTotalDebits] = useState(0);
  const [billPopup, setBillPopup] = useState<{ entry: BankAccountSummaryEntry } | null>(null);
  const [showSettled, setShowSettled] = useState(true);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    type?: 'danger' | 'warning' | 'success';
  } | null>(null);

  const [editingTitle, setEditingTitle] = useState<{ id: number; text: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [unlinkedEntries, setUnlinkedEntries] = useState<{ entries: any[], fileName: string, uploadedAt: string } | null>(null);
  const [isSettling, setIsSettling] = useState(false);

  // Filters
  const [filterMode, setFilterMode] = useState<string>('all');
  const [filterFile, setFilterFile] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async (pg: number = 1) => {
    if (!societyId) {
      setLoading(false);
      setError('No society selected. Please select a society from the menu.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await getBankAccountSummary(societyId, pg, PAGE_SIZE);
      setEntries(res.data.entries);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
      setTotalCredits(res.data.totalCredits);
      setTotalDebits(res.data.totalDebits);
      setPage(pg);
    } catch (err: any) {
      setError(err.message || 'Failed to load ledger data');
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  const handleUnlink = async (entryId: number) => {
    setConfirmModal({
      title: 'Unlink Transaction',
      message: 'Are you sure you want to unlink this bill? The bill will return to pending approval state.',
      onConfirm: async () => {
        try {
          await unlinkBill(entryId);
          loadData(page);
        } catch (err: any) {
          setError(err.message || 'Failed to unlink bill');
        }
      },
      type: 'danger'
    });
  };

  const handleUpdateTitle = async () => {
    if (!editingTitle) return;
    setIsSaving(true);
    try {
      await updateTransactionTitle(editingTitle.id, editingTitle.text);
      loadData(page);
      setEditingTitle(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update title');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSettleSummary = async (fileName: string, uploadedAt: string) => {
    setConfirmModal({
      title: 'Settle Statement',
      message: `Are you sure you want to mark all transactions in "${fileName}" as settled? This will finalize the reconciliation for this batch.`,
      confirmText: 'Yes, Settle All',
      type: 'success',
      onConfirm: async () => {
        setIsSettling(true);
        setError('');
        try {
          await markStatementAsSettled(societyId, fileName, uploadedAt);
          loadData(page);
          
          setConfirmModal({
            title: 'Settlement Successful',
            message: `Statement "${fileName}" has been settled successfully.`,
            confirmText: 'Great!',
            type: 'success',
            onConfirm: () => {}
          });
        } catch (err: any) {
          if (err.unlinkedEntries && err.unlinkedEntries.length > 0) {
            setUnlinkedEntries({ entries: err.unlinkedEntries, fileName, uploadedAt });
          } else {
            setError(err.message || 'Failed to settle statement');
          }
        } finally {
          setIsSettling(false);
        }
      }
    });
  };

  const handleBulkIgnoreAndSettle = async () => {
    if (!unlinkedEntries) return;
    setIsSettling(true);
    try {
      const ids = unlinkedEntries.entries.map(e => e.id);
      await bulkIgnoreTransactions(ids);
      // After ignoring, try settling again
      await markStatementAsSettled(societyId, unlinkedEntries.fileName, unlinkedEntries.uploadedAt);
      setUnlinkedEntries(null);
      loadData(page);
      
      setConfirmModal({
        title: 'Settlement Successful',
        message: 'The statement has been fully settled and reconciled successfully.',
        confirmText: 'Done',
        type: 'success',
        onConfirm: () => {}
      });
    } catch (err: any) {
      setError(err.message || 'Failed to bulk ignore and settle');
    } finally {
      setIsSettling(false);
    }
  };

  const handleIgnore = async (ledgerId: number, ignore: boolean) => {
    try {
      await ignoreTransaction(ledgerId, ignore);
      setSuccess(ignore ? 'Transaction ignored.' : 'Transaction restored.');
      loadData(page);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update transaction status');
    }
  };

  useEffect(() => {
    loadData(1);
  }, [loadData]);

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setProcessingPdf(true);
      setError('');
      try {
        const res = await parseStatementPdf(file);
        setParsedTransactions(res.data.transactions);
        setShowPreview(true);
      } catch (err: any) {
        setError(err.message || 'Failed to parse bank statement');
      } finally {
        setProcessingPdf(false);
        // Reset input value so same file can be uploaded again if needed
        e.target.value = '';
      }
    } else if (file) {
      setError('Please select a PDF file');
    }
  };

  const handleSaveBankSummary = async () => {
    if (!pdfFile || parsedTransactions.length === 0) return;
    setSavingSummary(true);
    setError('');
    try {
      await saveBankAccountSummary(societyId, {
        sourceFileName: pdfFile.name,
        bankAccountsSummary: parsedTransactions.map(txn => ({
          date: txn.date,
          particulars: txn.particulars,
          credit: txn.credit,
          debit: txn.debit,
          paymentMode: txn.paymentMode,
          transactionRefs: txn.transactionRefs,
        })),
      });
      setShowPreview(false);
      setPdfFile(null);
      setParsedTransactions([]);
      loadData(1); // Refresh ledger
    } catch (err: any) {
      setError(err.message || 'Failed to save ledger data');
    } finally {
      setSavingSummary(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  // Distinct source file names for filter dropdown
  const sourceFileNames = useMemo(() => {
    const names = new Set(entries.map(e => e.sourceFileName));
    return Array.from(names).sort();
  }, [entries]);

  // Filtered entries
  const filteredEntries = useMemo(() => {
    return entries.filter(e => {
      // Payment mode filter
      if (filterMode !== 'all') {
        const mode = (e.paymentMode || '').toLowerCase();
        if (filterMode === 'upi' && !mode.includes('upi')) return false;
        if (filterMode === 'neft' && !['neft', 'rtgs', 'imps'].some(k => mode.includes(k))) return false;
        if (filterMode === 'credit' && e.credit === null) return false;
        if (filterMode === 'debit' && e.debit === null) return false;
      }
      // Source file filter
      if (filterFile !== 'all' && e.sourceFileName !== filterFile) return false;
      // Search term
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matchParticulars = e.particulars?.toLowerCase().includes(q);
        const matchRefs = e.transactionRefs.some(r => r.toLowerCase().includes(q));
        const matchFile = e.sourceFileName.toLowerCase().includes(q);
        const matchDate = e.transactionDate.includes(q);
        if (!matchParticulars && !matchRefs && !matchFile && !matchDate) return false;
      }
      return true;
    });
  }, [entries, filterMode, filterFile, searchTerm]);

  // Group entries by source file for visual separation
  const groupedByFile = useMemo(() => {
    const map = new Map<string, { entries: BankAccountSummaryEntry[]; uploadedAt: string; bankSummaryId?: number | null; fileName: string }>();

    for (const entry of filteredEntries) {
      const timeBucket = entry.createdAt.substring(0, 16);
      const groupKey = `statement_${entry.sourceFileName}_${timeBucket}`;

      if (!map.has(groupKey)) {
        map.set(groupKey, {
          entries: [],
          uploadedAt: entry.createdAt,
          bankSummaryId: entry.id,
          fileName: entry.sourceFileName || 'Bank Statement',
        });
      }
      map.get(groupKey)!.entries.push(entry);
    }
    
    return Array.from(map.values()).sort((a, b) => 
      new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
    );
  }, [filteredEntries]);

  return (
    <div className="ledger-page">
      <div className="ledger-top-bar" style={{ justifyContent: 'flex-end' }}>
        <div className="ledger-top-actions">
          <button
            className="recon-btn recon-btn-primary"
            onClick={() => navigate('/society/reconcile')}
          >
            <HiChevronRight size={16} /> Reconciliation
          </button>
          <button
            className="recon-btn recon-btn-outline"
            onClick={() => loadData(page)}
            disabled={loading}
          >
            <HiRefresh size={16} /> {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Upload New Statement Dropzone */}
      <div className="ledger-upload-zone" style={{ marginBottom: '24px' }}>
        <input
          type="file"
          accept="application/pdf"
          onChange={handlePdfUpload}
          id="ledger-pdf-upload"
          style={{ display: 'none' }}
        />
        <label htmlFor="ledger-pdf-upload" style={{ 
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '30px', border: '2px dashed var(--tt-border)', borderRadius: 'var(--tt-radius)',
          backgroundColor: 'var(--tt-surface-2)', cursor: 'pointer', transition: 'all 0.2s',
          color: 'var(--tt-text-muted)', gap: '10px'
        }}>
          {processingPdf ? (
            <>
              <HiRefresh size={32} className="spin" />
              <span style={{ fontWeight: 600, fontSize: '1.1rem', color: 'var(--tt-text)' }}>Processing Bank Statement...</span>
              <small>Reading PDF and extracting transactions via OCR</small>
            </>
          ) : (
            <>
              <HiUpload size={32} />
              <span style={{ fontWeight: 600, fontSize: '1.1rem', color: 'var(--tt-text)' }}>Drop or click to upload new Bank Statement PDF</span>
              <small>PDF format only, max 10MB. OCR results will be shown for review.</small>
            </>
          )}
        </label>
      </div>

      {/* OCR Preview Modal */}
      {showPreview && (
        <div className="ledger-modal-overlay">
          <div className="ledger-modal ledger-modal-large">
            <div className="ledger-modal-header">
              <div className="ledger-modal-title-group">
                <h3>Review Statement: {pdfFile?.name}</h3>
                <span className="ledger-modal-subtitle">{parsedTransactions.length} transactions identified</span>
              </div>
              <button className="ledger-modal-close" onClick={() => setShowPreview(false)}>&times;</button>
            </div>
            <div className="ledger-modal-body">
              <div className="ledger-preview-table-container">
                <table className="ledger-preview-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Particulars</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Ref Numbers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedTransactions.map((txn, idx) => (
                      <tr key={idx}>
                        <td>{txn.date}</td>
                        <td className="cell-particulars">{txn.particulars}</td>
                        <td className="cell-amount">{txn.debit ? formatCurrency(txn.debit) : '-'}</td>
                        <td className="cell-amount">{txn.credit ? formatCurrency(txn.credit) : '-'}</td>
                        <td className="cell-mono">{txn.transactionRefs?.join(', ') || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="ledger-modal-footer">
              <button className="recon-btn recon-btn-outline" onClick={() => setShowPreview(false)}>Cancel</button>
              <button 
                className="recon-btn recon-btn-primary" 
                onClick={handleSaveBankSummary}
                disabled={savingSummary}
              >
                {savingSummary ? 'Saving to Ledger...' : 'Add to Ledger'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="recon-alert recon-alert-error">
          <HiExclamation size={18} /> {error}
          <button className="recon-alert-close" onClick={() => setError('')}>&times;</button>
        </div>
      )}
      {success && (
        <div className="recon-alert recon-alert-success" style={{ background: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' }}>
          <HiCheckCircle size={18} /> {success}
          <button className="recon-alert-close" onClick={() => setSuccess('')}>&times;</button>
        </div>
      )}

      {/* Summary KPIs */}
      <div className="ledger-kpi-grid">
        <div className="ledger-kpi-card">
          <div className="ledger-kpi-value">{total}</div>
          <div className="ledger-kpi-label">Total Entries</div>
        </div>
        <div className="ledger-kpi-card ledger-kpi-credit">
          <div className="ledger-kpi-value">{formatCurrency(totalCredits)}</div>
          <div className="ledger-kpi-label">Total Credits</div>
        </div>
        <div className="ledger-kpi-card ledger-kpi-debit">
          <div className="ledger-kpi-value">{formatCurrency(totalDebits)}</div>
          <div className="ledger-kpi-label">Total Debits</div>
        </div>
        <div className="ledger-kpi-card">
          <div className="ledger-kpi-value">{sourceFileNames.length}</div>
          <div className="ledger-kpi-label">Statements Uploaded</div>
        </div>
      </div>

      {/* Filters */}
      <div className="ledger-filters">
        <input
          type="text"
          className="ledger-search"
          placeholder="Search particulars, refs, dates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="ledger-filter-select"
          value={filterMode}
          onChange={(e) => setFilterMode(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="credit">Credits Only</option>
          <option value="debit">Debits Only</option>
          <option value="upi">UPI</option>
          <option value="neft">NEFT / IMPS / RTGS</option>
        </select>
        <select
          className="ledger-filter-select"
          value={filterFile}
          onChange={(e) => setFilterFile(e.target.value)}
        >
          <option value="all">All Statements</option>
          {sourceFileNames.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <span className="ledger-result-count">
          {filteredEntries.length} of {entries.length} entries
        </span>
        <button 
          className={`recon-btn recon-btn-sm ${showSettled ? 'recon-btn-outline' : 'recon-btn-primary'}`}
          onClick={() => setShowSettled(!showSettled)}
          style={{ marginLeft: 'auto' }}
        >
          {showSettled ? 'Hide Settled' : 'Show All'}
        </button>
      </div>

      {/* Grouped tables */}
      {loading && entries.length === 0 && (
        <div className="ledger-empty">
          <HiRefresh size={24} />
          <p>Loading ledger data...</p>
        </div>
      )}

      {!loading && entries.length === 0 && (
        <div className="ledger-empty">
          <HiDocumentText size={40} />
          <p>No bank statement data saved yet.</p>
          <button
            className="recon-btn recon-btn-primary"
            onClick={() => navigate('/society/reconciliation')}
          >
            Upload a Statement
          </button>
        </div>
      )}

      {groupedByFile
        .filter(group => showSettled || !group.entries.every(e => e.isSettled))
        .map((group, gi) => (
        <div key={gi} className="ledger-group">
          <div className="ledger-group-header">
            <div className="ledger-group-title-zone">
              <div className="ledger-group-title">
                <HiDocumentText size={18} />
                <span className="ledger-group-filename">{group.fileName}</span>
              </div>
              <div className="ledger-group-meta">
                {group.entries.length} entries · Uploaded {new Date(group.uploadedAt).toLocaleDateString('en-IN', {
                  day: '2-digit', month: 'short', year: 'numeric'
                })}
              </div>
            </div>
            
            <div className="ledger-group-actions-zone">
              {group.bankSummaryId && (
                <div className="ledger-settlement-controls">
                  {group.entries.every(e => e.isSettled) ? (
                    <div className="settled-status-badge">
                      <HiCheckCircle size={16} />
                      <span>Fully Settled</span>
                    </div>
                  ) : (
                    <button 
                      className="recon-btn recon-btn-success recon-btn-sm settle-group-btn"
                      onClick={() => handleSettleSummary(group.fileName, group.uploadedAt)}
                    >
                      <HiCheckCircle size={14} /> Mark Statement as Settled
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="recon-table-wrapper">
            <table className="recon-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Particulars</th>
                  <th>Debit</th>
                  <th>Credit</th>
                  <th className="col-bill-no">Payment #</th>
                  <th className="col-flat-no">Flat No.</th>
                  <th className="col-bill-amt">Bill Amt</th>
                  <th className="col-action" style={{ textAlign: 'left' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {group.entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className={`${entry.debit !== null && entry.credit === null ? 'ledger-row-debit' : ''} ${entry.linkedBill ? 'row-matched' : ''} ${entry.isSettled ? 'row-settled' : ''} ${entry.isIgnored ? 'row-ignored' : ''} source-bank`}
                  >
                    <td className="cell-mono">{entry.transactionDate}</td>
                    <td>
                      {editingTitle?.id === entry.id ? (
                        <div className="editing-particulars">
                          <input 
                            type="text" 
                            className="recon-input recon-input-sm"
                            placeholder="Enter custom title..."
                            value={editingTitle.text}
                            onChange={e => setEditingTitle({ ...editingTitle, text: e.target.value })}
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleUpdateTitle();
                              if (e.key === 'Escape') setEditingTitle(null);
                            }}
                          />
                          <div className="editing-actions">
                            <button onClick={handleUpdateTitle} disabled={isSaving}>
                              {isSaving ? '...' : <HiCheckCircle size={14} />}
                            </button>
                            <button onClick={() => setEditingTitle(null)} disabled={isSaving}>
                              <HiX size={14} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="particulars-display" 
                          onClick={() => setEditingTitle({ id: entry.id, text: entry.title || '' })}
                          title="Click to set custom title"
                        >
                          {entry.title || <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontStyle: 'italic' }}>+ Add Title</span>}
                        </div>
                      )}
                    </td>
                    <td className="cell-particulars" title={entry.particulars}>
                      {entry.particulars || '-'}
                    </td>
                    <td className="cell-amount cell-debit-text">
                      {entry.debit !== null ? formatCurrency(entry.debit) : '-'}
                    </td>
                    <td className="cell-amount cell-credit">
                      {entry.credit !== null ? formatCurrency(entry.credit) : '-'}
                    </td>
                    <td className="col-bill-no">
                      {entry.linkedBill ? (
                        <button 
                          className="cell-bill-id-btn" 
                          onClick={() => setBillPopup({ entry })}
                          title="View Bill Details"
                        >
                          #{entry.linkedPayment?.paymentId ?? entry.linkedBill.billId}
                        </button>
                      ) : '-'}
                    </td>
                    <td className="col-flat-no cell-mono">
                      {entry.linkedBill ? (
                        <span className="cell-flat-no" style={{ color: '#10b981', fontWeight: 700 }}>
                          {entry.linkedBill.blockName ? `${entry.linkedBill.blockName}-` : ''}{entry.linkedBill.flatNumber}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="col-bill-amt cell-amount" style={{ color: entry.linkedBill ? '#10b981' : 'inherit', fontWeight: entry.linkedBill ? 700 : 'normal' }}>
                      {entry.linkedBill ? formatCurrency(entry.linkedBill.amount) : '-'}
                    </td>
                    <td className="col-action">
                      <div className="dropdown-action-container">
                        <button 
                          className="action-menu-trigger"
                          onClick={() => setOpenActionMenuId(openActionMenuId === entry.id ? null : entry.id)}
                        >
                          <HiDotsVertical size={16} />
                        </button>

                        {openActionMenuId === entry.id && (
                          <>
                            <div className="action-menu-overlay" onClick={() => setOpenActionMenuId(null)} />
                            <div className="action-menu-dropdown">
                              <button onClick={() => { setBillPopup({ entry }); setOpenActionMenuId(null); }}>
                                <HiEye size={14} /> View Details
                              </button>
                              
                              {!entry.linkedBill && (
                                <button onClick={() => { handleIgnore(entry.id, !entry.isIgnored); setOpenActionMenuId(null); }}>
                                  {entry.isIgnored ? <HiEye size={14} /> : <HiEyeOff size={14} />}
                                  {entry.isIgnored ? 'Restore Entry' : 'Ignore Entry'}
                                </button>
                              )}

                              {entry.linkedBill && (
                                <button 
                                  className="text-red" 
                                  onClick={() => { 
                                    setOpenActionMenuId(null);
                                    handleUnlink(entry.id);
                                  }}
                                >
                                  <HiX size={14} /> Unlink Match
                                </button>
                              )}
                              
                              {!entry.linkedBill && entry.credit !== null && !entry.isIgnored && (
                                <button onClick={() => { navigate('/society/reconcile'); setOpenActionMenuId(null); }}>
                                  <HiSearch size={14} /> Go to Reconcile
                                </button>
                              )}

                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="ledger-pagination">
          <button
            className="recon-btn recon-btn-outline recon-btn-sm"
            disabled={page <= 1}
            onClick={() => loadData(page - 1)}
          >
            ← Previous
          </button>
          <span className="ledger-page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="recon-btn recon-btn-outline recon-btn-sm"
            disabled={page >= totalPages}
            onClick={() => loadData(page + 1)}
          >
            Next →
          </button>
        </div>
      )}
      {/* ── Bill Detail Popup ── */}
      {billPopup && billPopup.entry.linkedBill && (
        <div className="bill-popup-overlay" onClick={() => setBillPopup(null)}>
          <div className="bill-popup" onClick={e => e.stopPropagation()}>
            <div className="bill-popup-header">
              <h3>Linked Bill Details</h3>
              <button className="recon-modal-close" onClick={() => setBillPopup(null)}>&times;</button>
            </div>
            <div className="bill-popup-body">
              <div className="bill-popup-grid">
                <div className="bill-popup-section">
                  <h4>Bill Information</h4>
                  <table className="bill-popup-table">
                    <tbody>
                      <tr><td>Flat</td><td><strong>{billPopup.entry.linkedBill.blockName ? `${billPopup.entry.linkedBill.blockName}-` : ''}{billPopup.entry.linkedBill.flatNumber}</strong></td></tr>
                      <tr><td>Resident</td><td>{billPopup.entry.linkedBill.residentName || '-'}</td></tr>
                      <tr><td>Category</td><td>{billPopup.entry.linkedBill.billType ? <span className="recon-badge badge-blue">{billPopup.entry.linkedBill.billType}</span> : '-'}</td></tr>
                      <tr><td>Bill Amount</td><td className="cell-amount"><strong>{formatCurrency(billPopup.entry.linkedBill.amount)}</strong></td></tr>
                      <tr><td>Billing Date</td><td className="cell-mono">{billPopup.entry.linkedBill.billingDate}</td></tr>
                      <tr><td>Description</td><td style={{ fontSize: '0.8rem', color: '#64748b' }}>{billPopup.entry.linkedBill.remarks || '-'}</td></tr>
                      <tr><td>Bill ID</td><td className="cell-mono">#{billPopup.entry.linkedBill.billId}</td></tr>
                      <tr><td>Status</td><td><span className="recon-badge badge-green">{billPopup.entry.linkedBill.status}</span></td></tr>
                    </tbody>
                  </table>
                </div>

                <div className="bill-popup-section">
                  <h4>Bank Entry</h4>
                  <table className="bill-popup-table">
                    <tbody>
                      <tr><td>Date</td><td className="cell-mono">{billPopup.entry.transactionDate}</td></tr>
                      <tr><td>Particulars</td><td>{billPopup.entry.particulars || '-'}</td></tr>
                      <tr><td>Bank Amount</td><td className="cell-amount"><strong>{formatCurrency(billPopup.entry.credit || billPopup.entry.debit || 0)}</strong></td></tr>
                      <tr><td>Bank Mode</td><td>{billPopup.entry.paymentMode ? <span className="recon-badge badge-blue">{billPopup.entry.paymentMode}</span> : '-'}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {(billPopup.entry.linkedBill as any).screenshotFilePath && (
                <div className="bill-popup-section screenshot-section" style={{ marginTop: '20px' }}>
                  <h4 style={{ marginBottom: '10px' }}>Attached Screenshot</h4>
                  <div className="screenshot-container" style={{ cursor: 'pointer', position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <img 
                      src={`${API_BASE_URL.replace('/api', '')}${(billPopup.entry.linkedBill as any).screenshotFilePath}`} 
                      alt="Payment Proof" 
                      style={{ width: '100%', display: 'block', maxHeight: '300px', objectFit: 'contain', background: '#f8fafc' }}
                      onClick={() => window.open(`${API_BASE_URL.replace('/api', '')}${(billPopup.entry.linkedBill as any).screenshotFilePath}`, '_blank')}
                    />
                    <div className="screenshot-overlay">
                      <HiEye size={20} />
                      <span>View Full Size</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="bill-popup-actions">
                <button 
                  className="recon-btn recon-btn-outline recon-btn-sm btn-unlink"
                  style={{ color: '#dc2626', borderColor: '#fecaca' }}
                  onClick={() => { handleUnlink(billPopup.entry.id); setBillPopup(null); }}
                  title="Unlink Bill"
                >
                  <HiX size={14} /> Unlink This Bill
                </button>
                <button className="recon-btn recon-btn-outline" onClick={() => setBillPopup(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation Modal ── */}
      {confirmModal && (
        <div className="confirm-modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className={`confirm-modal-header ${confirmModal.type || 'warning'}`}>
              <h3>{confirmModal.title}</h3>
              <HiExclamation size={24} />
            </div>
            <div className="confirm-modal-body">
              <p>{confirmModal.message}</p>
            </div>
            <div className="confirm-modal-footer">
              <button className="recon-btn recon-btn-outline" onClick={() => setConfirmModal(null)}>Cancel</button>
              <button 
                className={`recon-btn ${
                  confirmModal.type === 'danger' ? 'recon-btn-danger' : 
                  confirmModal.type === 'success' ? 'recon-btn-success' : 
                  'recon-btn-warning'
                }`}
                onClick={() => {
                  confirmModal.onConfirm();
                  setConfirmModal(null);
                }}
              >
                {confirmModal.confirmText || 'Confirm Action'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Unlinked Entries Modal ── */}
      {unlinkedEntries && (
        <div className="confirm-modal-overlay" onClick={() => setUnlinkedEntries(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="confirm-modal-header warning">
              <h3>Unlinked Transactions Found</h3>
              <HiExclamation size={24} />
            </div>
            <div className="confirm-modal-body">
              <p style={{ marginBottom: '15px', color: '#64748b' }}>
                The following {unlinkedEntries.entries.length} transactions must be either linked to bills or ignored before you can settle this statement:
              </p>
              <div className="unlinked-list-scroll" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table className="recon-table" style={{ fontSize: '0.8rem' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: '8px' }}>Date</th>
                      <th style={{ padding: '8px' }}>Title/Particulars</th>
                      <th style={{ padding: '8px' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unlinkedEntries.entries.map((e: any) => (
                      <tr key={e.id}>
                        <td style={{ padding: '8px' }}>{e.date}</td>
                        <td style={{ padding: '8px' }}>
                          <div style={{ fontWeight: '600' }}>{e.title || 'Untitled'}</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{e.particulars}</div>
                        </td>
                        <td style={{ padding: '8px' }}>{formatCurrency(e.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="confirm-modal-footer" style={{ gap: '10px' }}>
              <button className="recon-btn recon-btn-outline" onClick={() => setUnlinkedEntries(null)}>Close</button>
              <button 
                className="recon-btn recon-btn-warning"
                onClick={handleBulkIgnoreAndSettle}
                disabled={isSettling}
              >
                {isSettling ? 'Processing...' : `Ignore All ${unlinkedEntries.entries.length} & Settle`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Global Settle Loader ── */}
      {isSettling && (
        <div className="confirm-modal-overlay" style={{ zIndex: 9999, background: 'rgba(255, 255, 255, 0.7)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
            <HiRefresh size={48} className="spin" style={{ color: '#0ea5e9' }} />
            <p style={{ fontWeight: '700', color: '#0369a1' }}>Processing Settlement...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocietyAdminLedger;
